package dev.danko.roblox_craft_ai.client

import dev.danko.roblox_craft_ai.uikit.config.BackendEndpoint
import dev.danko.roblox_craft_ai.shared.model.WsAssetUpdated
import dev.danko.roblox_craft_ai.shared.model.WsFrame
import dev.danko.roblox_craft_ai.shared.model.WsPing
import dev.danko.roblox_craft_ai.shared.model.WsPong
import dev.danko.roblox_craft_ai.shared.model.WsSubscribe
import dev.danko.roblox_craft_ai.shared.model.WsUnsubscribe
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.modules.SerializersModule
import kotlinx.serialization.modules.polymorphic
import kotlinx.serialization.modules.subclass
import kotlin.js.Date
import kotlin.math.pow
import kotlin.random.Random

private val wsJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = true
    isLenient = true
    classDiscriminator = "op"
    serializersModule = SerializersModule {
        polymorphic(WsFrame::class) {
            subclass(WsSubscribe::class)
            subclass(WsUnsubscribe::class)
            subclass(WsAssetUpdated::class)
            subclass(WsPing::class)
            subclass(WsPong::class)
            subclass(dev.danko.roblox_craft_ai.shared.model.WsError::class)
        }
    }
}

object RobloxCraftWsClient {

    private var webSocket: dynamic = null
    private var sessionToken: String? = null
    private val subscribedAssetIds: MutableSet<String> = mutableSetOf()
    private var reconnectAttempts: Int = 0
    private var reconnectTimer: Int? = null
    private var connectTimeoutTimer: Int? = null
    private var heartbeatTimer: Int? = null
    private var lastPongAt: Double = 0.0
    private var fallbackPollingTriggered = false

    private val _isConnectedFlow = MutableStateFlow(false)
    val isConnectedFlow: StateFlow<Boolean> = _isConnectedFlow.asStateFlow()

    private val _fallbackPollingFlow = MutableStateFlow(false)
    val fallbackPollingFlow: StateFlow<Boolean> = _fallbackPollingFlow.asStateFlow()

    private val _assetUpdatesFlow = MutableSharedFlow<WsAssetUpdated>(
        extraBufferCapacity = 50,
        onBufferOverflow = BufferOverflow.DROP_OLDEST
    )
    val assetUpdatesFlow: SharedFlow<WsAssetUpdated> = _assetUpdatesFlow.asSharedFlow()

    var onAssetUpdated: ((WsAssetUpdated) -> Unit)? = null
    var onFallbackPollingActivated: (() -> Unit)? = null

    private const val MAX_RECONNECT_ATTEMPTS_FOR_FALLBACK = 3
    private const val CONNECT_TIMEOUT_MS = 5000
    private const val HEARTBEAT_CHECK_INTERVAL_MS = 35000
    private const val HEARTBEAT_STALE_THRESHOLD_MS = 40000

    fun connect(token: String) {
        val hasWs = try {
            js("typeof WebSocket !== 'undefined'") as Boolean
        } catch (_: Throwable) {
            false
        }
        if (!hasWs) {
            activateFallbackPolling("WebSocket API not available")
            return
        }
        this.sessionToken = token
        fallbackPollingTriggered = false
        doConnect()
        startHeartbeatMonitor()
    }

    private fun buildWsUrl(): String = BackendEndpoint.ws("/ws/sync?token=${encodeURI(sessionToken ?: "")}")

    private fun doConnect() {
        clearAllTimers()
        val url = try {
            buildWsUrl()
        } catch (_: Throwable) {
            BackendEndpoint.ws("/ws/sync?token=${encodeURI(sessionToken ?: "")}")
        }
        try {
            val ws: dynamic = js("new WebSocket(url)")
            webSocket = ws
            val connectTimeoutTimerVal: Int = window.setTimeout({
                try {
                    webSocket?.close()
                } catch (_: Throwable) {}
                scheduleReconnect()
            }, CONNECT_TIMEOUT_MS)
            connectTimeoutTimer = connectTimeoutTimerVal
            ws.onopen = {
                clearConnectTimeout()
                _isConnectedFlow.value = true
                reconnectAttempts = 0
                fallbackPollingTriggered = false
                _fallbackPollingFlow.value = false
                lastPongAt = Date.now()
                if (subscribedAssetIds.isNotEmpty()) {
                    sendSubscribeInternal(subscribedAssetIds.toList())
                }
                Unit
            }
            ws.onclose = { event: dynamic ->
                val code = try { event.code as Int } catch (_: Throwable) { 1006 }
                _isConnectedFlow.value = false
                stopHeartbeatMonitor()
                if (code != 1008) {
                    scheduleReconnect()
                } else {
                    activateFallbackPolling("Server rejected token (code 1008)")
                }
                Unit
            }
            ws.onerror = {
                try {
                    webSocket?.close()
                } catch (_: Throwable) {}
                Unit
            }
            ws.onmessage = msgHandler@{ event: dynamic ->
                val data = try { event.data as String } catch (_: Throwable) { return@msgHandler }
                handleIncomingFrame(data)
                Unit
            }
        } catch (t: Throwable) {
            scheduleReconnect()
        }
    }

    private fun handleIncomingFrame(text: String) {
        try {
            val frame = wsJson.decodeFromString(WsFrame.serializer(), text)
            when (frame) {
                is WsAssetUpdated -> {
                    GlobalScope.launch {
                        _assetUpdatesFlow.emit(frame)
                    }
                    onAssetUpdated?.invoke(frame)
                }
                is WsPing -> {
                    lastPongAt = Date.now()
                    try {
                        val pongJson = wsJson.encodeToString(WsFrame.serializer(), WsPong())
                        webSocket?.send(pongJson)
                    } catch (_: Throwable) {}
                }
                is WsPong -> {
                    lastPongAt = Date.now()
                }
                else -> {}
            }
        } catch (_: Throwable) {}
    }

    private fun scheduleReconnect() {
        reconnectAttempts++
        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS_FOR_FALLBACK && !fallbackPollingTriggered) {
            activateFallbackPolling("$MAX_RECONNECT_ATTEMPTS_FOR_FALLBACK failed reconnect attempts")
        }
        val exponent = reconnectAttempts.coerceAtMost(10).toDouble()
        val baseInterval = 3000.0 * 2.0.pow(exponent)
        val jitter = (Random.nextDouble() - 0.5) * 1000.0
        val interval = (baseInterval + jitter).coerceAtMost(30000.0).toInt()
        val reconnectTimerVal: Int = window.setTimeout({
            doConnect()
        }, interval)
        reconnectTimer = reconnectTimerVal
    }

    private fun activateFallbackPolling(reason: String) {
        if (fallbackPollingTriggered) return
        fallbackPollingTriggered = true
        _fallbackPollingFlow.value = true
        val msg = "[RobloxCraftWsClient] WebSocket connect failed after $reconnectAttempts attempts, falling back to HTTP polling."
        try {
            window.asDynamic().console.warn(msg)
        } catch (_: Throwable) {}
        onFallbackPollingActivated?.invoke()
    }

    fun subscribe(ids: List<String>) {
        val newIds = ids.filter { it.isNotBlank() && !subscribedAssetIds.contains(it) }
        if (newIds.isEmpty()) return
        subscribedAssetIds.addAll(newIds)
        if (_isConnectedFlow.value) {
            sendSubscribeInternal(newIds)
        }
    }

    fun unsubscribe(ids: List<String>) {
        val toRemove = ids.filter { subscribedAssetIds.contains(it) }
        if (toRemove.isEmpty()) return
        subscribedAssetIds.removeAll(toRemove.toSet())
        if (_isConnectedFlow.value) {
            sendUnsubscribeInternal(toRemove)
        }
    }

    private fun sendSubscribeInternal(ids: List<String>) {
        try {
            if (ids.isEmpty()) return
            val frame = WsSubscribe(asset_ids = ids)
            val jsonText = wsJson.encodeToString(WsFrame.serializer(), frame)
            webSocket?.send(jsonText)
        } catch (_: Throwable) {}
    }

    private fun sendUnsubscribeInternal(ids: List<String>) {
        try {
            val frame = WsUnsubscribe(asset_ids = ids)
            val jsonText = wsJson.encodeToString(WsFrame.serializer(), frame)
            webSocket?.send(jsonText)
        } catch (_: Throwable) {}
    }

    fun disconnect() {
        clearAllTimers()
        try {
            webSocket?.close(1000)
        } catch (_: Throwable) {}
        webSocket = null
        _isConnectedFlow.value = false
    }

    private fun startHeartbeatMonitor() {
        stopHeartbeatMonitor()
        heartbeatTimer = window.setInterval({
            checkHeartbeat()
        }, HEARTBEAT_CHECK_INTERVAL_MS)
    }

    private fun stopHeartbeatMonitor() {
        val hb = heartbeatTimer
        if (hb != null) {
            try {
                window.clearInterval(hb)
            } catch (_: Throwable) {}
            heartbeatTimer = null
        }
    }

    private fun checkHeartbeat() {
        if (!_isConnectedFlow.value) return
        val now = Date.now()
        if (now - lastPongAt > HEARTBEAT_STALE_THRESHOLD_MS) {
            try {
                webSocket?.close(1001)
            } catch (_: Throwable) {}
        }
    }

    private fun clearAllTimers() {
        clearConnectTimeout()
        val rt = reconnectTimer
        if (rt != null) {
            try {
                window.clearTimeout(rt)
            } catch (_: Throwable) {}
            reconnectTimer = null
        }
        stopHeartbeatMonitor()
    }

    private fun clearConnectTimeout() {
        val ct = connectTimeoutTimer
        if (ct != null) {
            try {
                window.clearTimeout(ct)
            } catch (_: Throwable) {}
            connectTimeoutTimer = null
        }
    }

    private fun encodeURI(v: String): String {
        return try {
            js("encodeURIComponent(v)") as String
        } catch (_: Throwable) {
            v
        }
    }
}
